import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import debounce from "lodash/debounce";
import {
  ADD_FIRM,
  ADD_INVESTMENT,
  CAN_CREATE_ENTITY,
  ENTITY_CONFIG,
  FIRM_NEAR_MATCHES,
  INVESTMENT_CREATE_ENUMS,
  INVESTMENT_NEAR_MATCHES,
} from "./entityQueries";

/**
 * Entity types the document queue can create inline (issue #5017). Only firm
 * and investment are supported — company / deal / firmEngagement stay
 * search-only until the queue's users ask for them.
 */
export const CREATABLE_RESOURCES = ["firm", "investment"] as const;
export type CreatableResource = (typeof CREATABLE_RESOURCES)[number];

const CREATE_MUTATION_NAME: Record<CreatableResource, string> = {
  firm: "addFirm",
  investment: "addInvestment",
};

/**
 * `INVESTMENT_UPDATE_DATA_MANAGER_FIRM_ID` destructures `args.input`, so the
 * accessibility probe has to carry an input object even though it is empty.
 */
const EMPTY_MUTATION_ARGS = JSON.stringify({ input: {} });

/** Minimum characters before searching — matches EntitySelector's threshold. */
const MIN_SEARCH_LENGTH = 2;

const NEAR_MATCH_LIMIT = 5;

/** Sentinel id for the "Add …" row in the nested parent-firm picker. */
const CREATE_FIRM_OPTION_ID = -1;

const RESOURCE_LABEL: Record<CreatableResource, string> = {
  firm: "Firm",
  investment: "Investment",
};

/**
 * AssetClassEnum has exactly two rows — "Hedge Fund" (1) and "Private Equity"
 * (2) — and every other Market picker relabels them Public / Private via
 * rcg-diligence's `marketTranslation`. That util is not reachable from
 * shared/frontend, so the same two-value mapping lives here.
 */
const PRIVATE_MARKET_ENUM_ID = 2;
const marketLabel = (id: number) =>
  id === PRIVATE_MARKET_ENUM_ID ? "Private" : "Public";

export interface CreatedEntity {
  id: number;
  name: string;
  firm?: { id: number; name: string } | null;
}

export function isCreatableResource(
  resource: string
): resource is CreatableResource {
  return (CREATABLE_RESOURCES as readonly string[]).includes(resource);
}

/**
 * True when the current user may run the create mutation behind `resource`.
 * Defaults to false while the probe is in flight: the create affordance is
 * additive, so hiding it until entitlement is confirmed is the safe direction.
 */
export function useCanCreateEntity(resource: string): boolean {
  const mutationName = isCreatableResource(resource)
    ? CREATE_MUTATION_NAME[resource]
    : undefined;

  const { data } = useQuery(CAN_CREATE_ENTITY, {
    variables: { mutationName, mutationArgs: EMPTY_MUTATION_ARGS },
    skip: !mutationName,
    fetchPolicy: "cache-first",
  });

  return !!data?.userMutationAccessible?.canAccess;
}

interface FirmOption {
  id: number;
  name: string;
  isCreateOption?: boolean;
}

interface EntityCreateDialogProps {
  resource: CreatableResource;
  /** Pre-fills the name field — the term the user searched for. */
  initialName: string;
  /** Document date from the queue row (YYYY-MM-DD); pre-fills inception date. */
  documentDate?: string | null;
  /** Firm already selected on the row, pre-selected as the investment's parent. */
  initialFirm?: { id: number; name: string } | null;
  onClose: () => void;
  /** Called with the entity to put in the row — created, or an existing near match. */
  onCreated: (entity: CreatedEntity) => void;
}

export const EntityCreateDialog: React.FC<EntityCreateDialogProps> = ({
  resource,
  initialName,
  documentDate,
  initialFirm,
  onClose,
  onCreated,
}) => {
  const isInvestment = resource === "investment";
  const label = RESOURCE_LABEL[resource];

  const [name, setName] = useState(initialName);
  const [nearMatchTerm, setNearMatchTerm] = useState(initialName);
  const [firm, setFirm] = useState<{ id: number; name: string } | null>(
    initialFirm ?? null
  );
  const [firmSearchTerm, setFirmSearchTerm] = useState("");
  const [createFirmName, setCreateFirmName] = useState<string | null>(null);
  const [listEnumId, setListEnumId] = useState<number | "">("");
  const [marketEnumId, setMarketEnumId] = useState<number | "">("");
  const [strategyEnumId, setStrategyEnumId] = useState<number | "">("");
  const [inceptionDate, setInceptionDate] = useState(documentDate ?? "");

  const debouncedNearMatchTerm = useMemo(
    () => debounce((term: string) => setNearMatchTerm(term), 300),
    []
  );

  const trimmedName = name.trim();
  const trimmedNearMatchTerm = nearMatchTerm.trim();
  const skipNearMatches = trimmedNearMatchTerm.length < MIN_SEARCH_LENGTH;

  const {
    data: nearMatchData,
    loading: nearMatchLoading,
    error: nearMatchError,
  } = useQuery(isInvestment ? INVESTMENT_NEAR_MATCHES : FIRM_NEAR_MATCHES, {
    variables: { name: trimmedNearMatchTerm, limit: NEAR_MATCH_LIMIT },
    skip: skipNearMatches,
    fetchPolicy: "network-only",
  });

  const nearMatches: CreatedEntity[] = isInvestment
    ? (nearMatchData?.investmentList?.items ?? [])
    : (nearMatchData?.firmList?.items ?? []);

  const { data: enumData, loading: enumsLoading } = useQuery(
    INVESTMENT_CREATE_ENUMS,
    { skip: !isInvestment, fetchPolicy: "cache-first" }
  );

  const { data: firmSearchData, loading: firmSearchLoading } = useQuery(
    ENTITY_CONFIG.firm.searchQuery,
    {
      variables: { searchTerm: firmSearchTerm },
      skip: !isInvestment || firmSearchTerm.length < MIN_SEARCH_LENGTH,
      fetchPolicy: "network-only",
    }
  );

  const canCreateFirm = useCanCreateEntity("firm");

  const firmOptions = useMemo(() => {
    const options: FirmOption[] = [];
    if (firm) options.push(firm);
    for (const item of firmSearchData?.firmList?.items ?? []) {
      if (item.id !== firm?.id) options.push(item);
    }
    if (canCreateFirm && firmSearchTerm.trim().length >= MIN_SEARCH_LENGTH) {
      options.push({
        id: CREATE_FIRM_OPTION_ID,
        name: firmSearchTerm.trim(),
        isCreateOption: true,
      });
    }
    return options;
  }, [firm, firmSearchData, canCreateFirm, firmSearchTerm]);

  // onError keeps the mutate promise from rejecting in the click handler — the
  // message is rendered from the hook's error state in the Alert below.
  const noThrow = { onError: () => undefined };
  const [addFirm, { loading: addingFirm, error: addFirmError }] = useMutation(
    ADD_FIRM,
    noThrow
  );
  const [
    addInvestment,
    { loading: addingInvestment, error: addInvestmentError },
  ] = useMutation(ADD_INVESTMENT, noThrow);

  const saving = addingFirm || addingInvestment;
  const error = addFirmError ?? addInvestmentError ?? nearMatchError;

  const missingRequired = isInvestment
    ? !trimmedName ||
      !firm ||
      listEnumId === "" ||
      marketEnumId === "" ||
      strategyEnumId === ""
    : !trimmedName;

  // The near-match list is the duplicate guard, so creation stays blocked until
  // the matches on screen are the ones for the name actually being submitted —
  // and never proceeds on a lookup that was skipped or failed, which would
  // claim "no matches" without having looked.
  const nearMatchesStale = trimmedName !== trimmedNearMatchTerm;
  const createDisabled =
    missingRequired ||
    saving ||
    nearMatchLoading ||
    nearMatchesStale ||
    skipNearMatches ||
    !!nearMatchError;

  const handleCreate = async () => {
    if (isInvestment) {
      if (!firm) return;
      const { data } = await addInvestment({
        variables: {
          input: {
            firmId: firm.id,
            name: trimmedName,
            listEnumId,
            marketEnumId,
            strategyEnumId,
            // Funds.IsPartner is NOT NULL with no DB default, so it has to be
            // sent explicitly — as createInvestment and Data Collection do.
            isPartner: false,
            ...(inceptionDate ? { inceptionDate } : {}),
          },
        },
      });
      if (data?.addInvestment) onCreated(data.addInvestment);
      return;
    }

    const { data } = await addFirm({
      // Firms.IsPartner is NOT NULL with no DB default — same as above.
      variables: { input: { name: trimmedName, isPartner: false } },
    });
    if (data?.addFirm) onCreated(data.addFirm);
  };

  const enumSelect = (
    dataCy: string,
    fieldLabel: string,
    value: number | "",
    setValue: (id: number) => void,
    items: { id: number; name: string }[]
  ) => (
    <Grid item xs={12} sm={4} data-cy={dataCy}>
      <TextField
        select
        fullWidth
        size="small"
        required
        label={fieldLabel}
        disabled={enumsLoading}
        value={value}
        onChange={e => setValue(Number(e.target.value))}
      >
        {items.map(item => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
    </Grid>
  );

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create {label}</DialogTitle>
        <DialogContent data-cy="entity-create-dialog">
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                fullWidth
                size="small"
                required
                label="Name"
                inputProps={{ "data-cy": "entity-create-name" }}
                value={name}
                onChange={e => {
                  setName(e.target.value);
                  debouncedNearMatchTerm(e.target.value);
                }}
              />
            </Grid>

            {isInvestment && (
              <>
                <Grid item xs={12}>
                  <Autocomplete<FirmOption>
                    size="small"
                    options={firmOptions}
                    loading={firmSearchLoading}
                    filterOptions={x => x}
                    value={firm}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    getOptionLabel={option =>
                      option?.isCreateOption
                        ? `Add "${option.name}"`
                        : (option?.name ?? "")
                    }
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") setFirmSearchTerm(value);
                    }}
                    onChange={(_, value) => {
                      if (value?.isCreateOption) {
                        setCreateFirmName(value.name);
                        return;
                      }
                      setFirm(value);
                    }}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        key={option.id}
                        data-cy={
                          option.isCreateOption
                            ? "entity-create-firm-create-option"
                            : undefined
                        }
                      >
                        {option.isCreateOption
                          ? `Add "${option.name}"`
                          : option.name}
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        required
                        label="Firm"
                        size="small"
                        inputProps={{
                          ...params.inputProps,
                          "data-cy": "entity-create-firm",
                        }}
                      />
                    )}
                  />
                </Grid>

                {enumSelect(
                  "entity-create-list",
                  "Investment List",
                  listEnumId,
                  setListEnumId,
                  enumData?.investmentListEnumList?.items ?? []
                )}
                {enumSelect(
                  "entity-create-market",
                  "Market",
                  marketEnumId,
                  setMarketEnumId,
                  (
                    (enumData?.assetClassEnumList?.items ?? []) as {
                      id: number;
                    }[]
                  ).map(item => ({ id: item.id, name: marketLabel(item.id) }))
                )}
                {enumSelect(
                  "entity-create-strategy",
                  "Strategy",
                  strategyEnumId,
                  setStrategyEnumId,
                  enumData?.strategyEnumList?.items ?? []
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Inception Date"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ "data-cy": "entity-create-inception-date" }}
                    value={inceptionDate}
                    onChange={e => setInceptionDate(e.target.value)}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} data-cy="entity-create-near-matches">
              {nearMatchLoading ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <CircularProgress size={12} />
                  Checking for existing records…
                </Typography>
              ) : nearMatches.length > 0 ? (
                <>
                  <Typography variant="caption" color="warning.main">
                    {`Possible existing ${label.toLowerCase()} records — select one instead of creating a duplicate:`}
                  </Typography>
                  <List dense disablePadding>
                    {nearMatches.map(match => (
                      <ListItemButton
                        key={match.id}
                        data-cy="entity-create-near-match"
                        onClick={() => onCreated(match)}
                      >
                        <ListItemText
                          primary={match.name}
                          secondary={match.firm?.name}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </>
              ) : (
                // Only claim "no matches" when the lookup actually ran and
                // succeeded — the error is reported in the Alert below.
                !skipNearMatches &&
                !nearMatchError && (
                  <Typography variant="caption" color="text.secondary">
                    {`No existing ${label.toLowerCase()} matches "${trimmedNearMatchTerm}".`}
                  </Typography>
                )
              )}
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Alert severity="error" data-cy="entity-create-error">
                  {error.message}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={onClose} data-cy="entity-create-cancel">
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            data-cy="entity-create-submit"
            disabled={createDisabled}
            onClick={handleCreate}
          >
            {saving && <CircularProgress size={14} sx={{ mr: 1 }} />}
            {nearMatches.length > 0 ? "Create anyway" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {createFirmName !== null && (
        <EntityCreateDialog
          resource="firm"
          initialName={createFirmName}
          onClose={() => setCreateFirmName(null)}
          onCreated={created => {
            setFirm({ id: created.id, name: created.name });
            setFirmSearchTerm("");
            setCreateFirmName(null);
          }}
        />
      )}
    </>
  );
};

export default EntityCreateDialog;
